import React from "react";
import PropTypes from "prop-types";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { InputBase } from "@mui/material";
import "../modal.css";

export default function OrganisationModel({ show, handleClose, onSave, editingData }) {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [nameError, setNameError] = React.useState("");
  const [descriptionError, setDescriptionError] = React.useState("");

  // New organization settings fields
  const [storageType, setStorageType] = React.useState("BLOB"); // Default to BLOB
  const [autoSuspendUserAfterDays, setAutoSuspendUserAfterDays] = React.useState(10);

  React.useEffect(() => {
    if (editingData) {
      console.log("editingData in model", editingData);
      setDescription(editingData.description);
      setName(editingData.name);
      setStorageType(editingData.storageType || "BLOB"); // Default to BLOB
      setAutoSuspendUserAfterDays(editingData.autoSuspendUserAfterDays || 10);
    } else {
      setDescription("");
      setName("");
      setStorageType("BLOB"); // Default to BLOB
      setAutoSuspendUserAfterDays(10);
    }
    // Clear errors when modal opens
    setNameError("");
    setDescriptionError("");
  }, [editingData, show]);

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name || name.trim() === "") {
      setNameError("Name is required");
      isValid = false;
    } else if (name.trim().length < 2) {
      setNameError("Name must be at least 2 characters long");
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate description
    if (!description || description.trim() === "") {
      setDescriptionError("Description is required");
      isValid = false;
    } else if (description.trim().length < 5) {
      setDescriptionError("Description must be at least 5 characters long");
      isValid = false;
    } else {
      setDescriptionError("");
    }

    return isValid;
  };

  const handleSaveClick = () => {
    if (validateForm()) {
      // Get user info from localStorage
      const userData = JSON.parse(localStorage.getItem("token")) || {};
      const userEmail = userData.email || "";

      const newRow = {
        name: name.trim(),
        description: description.trim(),
        createdBy: userEmail,
        storageType: storageType || "BLOB", // Default to BLOB
        pythonAPI: "", // Keep empty as requested
        autoSuspendUserAfterDays: parseInt(autoSuspendUserAfterDays) || 90,
      };
      onSave(newRow);
    }
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    if (nameError) {
      setNameError("");
    }
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    if (descriptionError) {
      setDescriptionError("");
    }
  };

  return (
    <Modal open={show} onClose={handleClose} aria-labelledby="add-administrator-modal-title">
      <div className="user-modal modal-container">
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          <span className="modal-title" style={{ fontWeight: 500 }}>
            {editingData ? "Edit Organization" : "Add New Organization"}
          </span>
          <button onClick={handleClose} className="modal-close-btn" aria-label="Close">
            &#x2715;
          </button>
        </div>

        <div>
          {/* Name */}
          <div className="modal-field-wrap">
            <label className="modal-label">
              Name <span style={{ color: "red" }}>*</span>
            </label>
            <InputBase
              value={name}
              onChange={handleNameChange}
              className="modal-input"
              style={{ border: nameError ? "1px solid #f44336" : "1px solid #E5E7EB" }}
              inputProps={{ style: { padding: 0 } }}
              placeholder="Organization name"
            />
            {nameError && (
              <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px" }}>
                {nameError}
              </span>
            )}
          </div>

          {/* Description */}
          <div className="modal-field-wrap">
            <label className="modal-label">
              Description <span style={{ color: "red" }}>*</span>
            </label>
            <InputBase
              value={description}
              onChange={handleDescriptionChange}
              className="modal-input"
              style={{ border: descriptionError ? "1px solid #f44336" : "1px solid #E5E7EB" }}
              inputProps={{ style: { padding: 0 } }}
              placeholder="Description"
            />
            {descriptionError && (
              <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px" }}>
                {descriptionError}
              </span>
            )}
          </div>

          {/* Storage Type */}
          <div className="modal-field-wrap">
            <label className="modal-label">
              Storage Type <span style={{ color: "red" }}>*</span>
            </label>
            <RadioGroup value={storageType} onChange={(e) => setStorageType(e.target.value)} row>
              <FormControlLabel
                value="BLOB"
                control={<Radio style={{ color: "#2aa7e0" }} />}
                label="BLOB Storage"
              />
              <FormControlLabel
                value="SQL"
                control={<Radio style={{ color: "#2aa7e0" }} />}
                label="SQL Database"
              />
            </RadioGroup>
          </div>

          {/* Auto Suspend Days */}
          <div className="modal-field-wrap">
            <label className="modal-label">Auto Suspend User After Days</label>
            <InputBase
              type="number"
              value={autoSuspendUserAfterDays}
              onChange={(e) => setAutoSuspendUserAfterDays(e.target.value)}
              className="modal-input"
              inputProps={{ style: { padding: 0 } }}
            />
          </div>

          {/* Buttons */}
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}
          >
            <Button variant="outlined" onClick={handleClose} className="modal-btn-cancel">
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveClick} className="modal-btn-save">
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

OrganisationModel.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editingData: PropTypes.object,
};
